import { Controller } from "@hotwired/stimulus";
import { buildUploadHandler } from "../utils/cloudinary";
import { validatesFormatAndSize } from "../utils/upload/validations";

export default class extends Controller {
  static targets = ["input", "bar", "dropzone"];
  static classes = ["dragging"];
  static values = {
    acceptVideo: Boolean,
  };
  static outlets = ["media-input--local", "media-input--turbo", "media-selector"]



  // Gestion des événements de drag & drop
  dragenter(event) {
    event.preventDefault();
    this.dragCounter = (this.dragCounter || 0) + 1;
    if (!this.uploadingValue) {
      this.dropzoneTarget.classList.add(...this.draggingClasses);
    }
  }

  dragleave(event) {
    event.preventDefault();
    this.dragCounter = (this.dragCounter || 1) - 1;
    if (this.dragCounter === 0 && !this.uploadingValue) {
      this.dropzoneTarget.classList.remove(...this.draggingClasses);
    }
  }

  dragover(event) {
    event.preventDefault();
  }

  drop(event) {
    event.preventDefault();
    // On vérifie les formats
    const forbiddenFiles = validatesFormatAndSize(this.inputTarget.files);
    if (forbiddenFiles.length > 0) return;
    this.dragCounter = 0;
    if (!this.uploadingValue) {
      this.dropzoneTarget.classList.remove(...this.draggingClasses);
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.inputTarget.files = files;
        this.handleFiles(files);
      }
    }
  }

  // Gestion des fichiers sélectionnés via l'input
  change(_event) {
    const files = this.inputTarget.files;
    // On vérifie les formats
    const forbiddenFiles = validatesFormatAndSize(this.inputTarget.files);
    if (forbiddenFiles.length > 0) return;
    if (files.length > 0) {
      this.handleFiles(files);
    }
  }

  // Traitement des fichiers sélectionnés
  handleFiles(files) {
    const filesArray = Array.from(files);
    let shouldUpload = true;

    // On bloque les fichiers qui ne respectent pas les formats autorisés
    filesArray.forEach((file) => {
      if (!this.acceptVideoValue && file.type.startsWith("video/")) {
        alert(
          "Les fichiers vidéos ne sont pas autorisés. Seuls les images sont autorisées au format suivants : png, jpg, jpeg, gif, webp et pdf."
        );
        shouldUpload = false;
      }
    });
    if (shouldUpload) {
      // On retient la taille du lot pour ne fermer la modale qu'à la fin.
      this.expectedFiles = filesArray.length;
      this.settledFiles = 0;
      this.failedKeys = new Set();
      // Le lot est annoncé AU DÉPÔT, avant tout envoi : qui l'écoute (la toolbox) y attache
      // ce qui doit valoir à l'arrivée ; chaque fichier repart avec l'id de son lot.
      const batch = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      this.dispatch("batch", { detail: { batch, count: filesArray.length } });
      this.currentBatch = batch;
      this.buildUploader(batch)(filesArray);
    }
  }

  get handler() {
    if (this.hasMediaInputLocalOutlet) {
      return this.mediaInputLocalOutlet
    }
    if (this.hasMediaSelectorOutlet) {
      return this.mediaSelectorOutlet
    }
    if (this.hasMediaInputTurboOutlet) {
      return this.mediaInputTurboOutlet
    }
  }

  async addFile(file, batch, order) {
    if (!this.handler?.onFileAdded) {
      // Sortie silencieuse historique : c'est ici que les photos disparaissaient
      // sans laisser la moindre trace. Elle reste un filet de sécurité, mais
      // elle parle désormais.
      console.error("file_uploader: aucun outlet disponible, fichier ignoré", file);
      return;
    }
    // `finally` : un média que le serveur refuse compte quand même dans le lot, sans quoi
    // la fin de lot n'arrive jamais.
    try {
      await this.handler.onFileAdded(file, { batch, order })
    } finally {
      this.settleFile(batch)
    }
  }

  // Un fichier du lot est réglé — arrivé ou en échec. Le lot se termine quand TOUS le sont :
  // tant qu'il en reste, on laisse la modale ouverte (la fermer vide le turbo-frame et emporte
  // l'outlet avec lui). La fin de lot est annoncée au handler (batchSettled) avant de fermer.
  settleFile(batch) {
    this.settledFiles = (this.settledFiles || 0) + 1;
    if (this.settledFiles < (this.expectedFiles || 1)) return;

    this.handler?.batchSettled?.(batch)
    this.handler?.closeDialog?.()
    this.terminateLoading()
  }

  // On retient les infos sur plusieurs uplaods en même temps et on retient le plus bas
  updateFileStatus(signature, status) {
    // Un échec règle son fichier, une seule fois : un XHR coupé passe par le listener
    // "error" ET par onreadystatechange.
    if (status.status === "errored" && !this.failedKeys?.has(signature)) {
      this.failedKeys?.add(signature);
      this.settleFile(this.currentBatch);
    }
    if (!this.statuses) {
      this.statuses = {};
      this.barTarget.classList.remove("hidden");
    }
    this.statuses[signature] = status;

    const percent = Math.min(
      ...Object.values(this.statuses).map((s) => s.progress)
    );

    // Upload réel → 0-50%, puis progression lente vers 99% en attendant le traitement Cloudinary
    const displayed = Math.min(percent * 0.5, 50);
    this.barTarget.style.width = `${displayed}%`;

    if (percent >= 100 && !this.slowProgressTimer) {
      this.slowProgressValue = 50;
      this.slowProgressTimer = setInterval(() => {
        this.slowProgressValue += (99 - this.slowProgressValue) * 0.05;
        this.barTarget.style.width = `${this.slowProgressValue}%`;
        if (this.slowProgressValue >= 98.9) clearInterval(this.slowProgressTimer);
      }, 200);
    }
  }

  terminateLoading() {
    if (this.slowProgressTimer) {
      clearInterval(this.slowProgressTimer);
      this.slowProgressTimer = null;
    }
    this.barTarget.style.width = "100%";
    setTimeout(() => {
      this.barTarget.classList.add("hidden");
      this.barTarget.style.width = "0%";
      this.statuses = null;
    }, 300);
  }

  buildUploader(batch) {
    return buildUploadHandler({
      // `order` : le rang du fichier dans la sélection, pas dans l'arrivée réseau.
      addFile: (file, order) => this.addFile(file, batch, order),
      updateFileStatus: this.updateFileStatus.bind(this),
    });
  }
}
